import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import GUI from 'lil-gui'
import { RectAreaLightHelper } from 'three/examples/jsm/helpers/RectAreaLightHelper.js'

/**
 * Base
 */
// Debug
const gui = new GUI()


// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Lights
 */

const ambientLight = new THREE.AmbientLight()
ambientLight.color = new THREE.Color(0xffffff)
ambientLight.intensity = 0.5
scene.add(ambientLight)

const ambientLightFolder = gui.addFolder('Ambient Light')
ambientLightFolder.add(ambientLight, 'visible')
ambientLightFolder.add(ambientLight, 'intensity').min(0).max(3).step(0.01)
ambientLightFolder.addColor(ambientLight, 'color')
ambientLightFolder.close()

const directionalLight = new THREE.DirectionalLight(0x00fffc, 0.9)
directionalLight.position.set(1, 0.25, 0)
scene.add(directionalLight)

const directionalLightFolder = gui.addFolder('Directional Light')
directionalLightFolder.add(directionalLight, 'visible')
directionalLightFolder.add(directionalLight, 'intensity').min(0).max(3).step(0.01)
directionalLightFolder.addColor(directionalLight, 'color')
directionalLightFolder.add(directionalLight.position, 'x').min(- 5).max(5).step(0.01).name('L/R')
directionalLightFolder.add(directionalLight.position, 'y').min(- 5).max(5).step(0.01).name('U/D')
directionalLightFolder.add(directionalLight.position, 'z').min(- 5).max(5).step(0.01).name('F/B')
directionalLightFolder.close()

const hemisphereLight = new THREE.HemisphereLight(0xff0000, 0x0000ff, 0.9)
scene.add(hemisphereLight)

const hemisphereLightFolder = gui.addFolder('Hemisphere Light')
hemisphereLightFolder.add(hemisphereLight, 'visible')
hemisphereLightFolder.add(hemisphereLight, 'intensity').min(0).max(3).step(0.01)
hemisphereLightFolder.addColor(hemisphereLight, 'color')
hemisphereLightFolder.addColor(hemisphereLight, 'groundColor')
hemisphereLightFolder.close()

const pointLight = new THREE.PointLight(0xff9900, 0.5, 10, 2)
pointLight.position.set(1, -0.5, 1)
scene.add(pointLight)

const pointLightFolder = gui.addFolder('Point Light')
pointLightFolder.add(pointLight, 'visible')
pointLightFolder.add(pointLight, 'intensity').min(0).max(3).step(0.01)
pointLightFolder.addColor(pointLight, 'color')
pointLightFolder.add(pointLight, 'distance').min(0).max(5).step(0.01)
pointLightFolder.add(pointLight, 'decay').min(0).max(5).step(0.01)
pointLightFolder.add(pointLight.position, 'x').min(- 5).max(5).step(0.01).name('L/R')
pointLightFolder.add(pointLight.position, 'y').min(- 5).max(5).step(0.01).name('U/D')
pointLightFolder.add(pointLight.position, 'z').min(- 5).max(5).step(0.01).name('F/B')
pointLightFolder.close()

const rectAreaLight = new THREE.RectAreaLight(0x4e00ff, 2, 1, 1) // solo funciona en standart y physical mesh materials
rectAreaLight.position.set(-1.5, 0, 1.5)
rectAreaLight.lookAt(new THREE.Vector3())
scene.add(rectAreaLight)

const rectAreaLightFolder = gui.addFolder('RectArea Light')
rectAreaLightFolder.add(rectAreaLight, 'visible')
rectAreaLightFolder.add(rectAreaLight, 'intensity').min(0).max(10).step(0.01)
rectAreaLightFolder.addColor(rectAreaLight, 'color')
rectAreaLightFolder.add(rectAreaLight, 'width').min(0).max(5).step(0.01)
rectAreaLightFolder.add(rectAreaLight, 'height').min(0).max(5).step(0.01)
rectAreaLightFolder.add(rectAreaLight.position, 'x').min(- 5).max(5).step(0.01).name('L/R')
rectAreaLightFolder.add(rectAreaLight.position, 'y').min(- 5).max(5).step(0.01).name('U/D')
rectAreaLightFolder.add(rectAreaLight.position, 'z').min(- 5).max(5).step(0.01).name('F/B')
rectAreaLightFolder.close()

const spotLight = new THREE.SpotLight(0xffffff, 4.5, 10, Math.PI * 0.1, 0.25, 1)
spotLight.position.set(0, 2, 3)
scene.add(spotLight)
scene.add(spotLight.target)
spotLight.target.position.set(-0.7, 0, 1)

const spotLightFolder = gui.addFolder('Spot Light')
spotLightFolder.add(spotLight, 'visible')
spotLightFolder.add(spotLight, 'intensity').min(0).max(10).step(0.01)
spotLightFolder.addColor(spotLight, 'color')
spotLightFolder.add(spotLight, 'distance').min(0).max(5).step(0.01)
spotLightFolder.add(spotLight, 'angle').min(0).max(Math.PI * 1.5).step(0.01)
spotLightFolder.add(spotLight, 'penumbra').min(0).max(1).step(0.01)
spotLightFolder.add(spotLight, 'decay').min(0).max(5).step(0.01)
spotLightFolder.add(spotLight.position, 'x').min(- 5).max(5).step(0.01).name('Rotation L/R')
spotLightFolder.add(spotLight.position, 'y').min(- 5).max(5).step(0.01).name('Rotation U/D')
spotLightFolder.add(spotLight.position, 'z').min(- 5).max(5).step(0.01).name('Rotation F/B')
spotLightFolder.add(spotLight.target.position, 'x').min(- 5).max(5).step(0.01).name('Target L/R')
spotLightFolder.add(spotLight.target.position, 'y').min(- 5).max(5).step(0.01).name('Target U/D')
spotLightFolder.add(spotLight.target.position, 'z').min(- 5).max(5).step(0.01).name('Target F/B')
spotLightFolder.close()

// helpers
const hemisphereLightHelper = new THREE.HemisphereLightHelper(hemisphereLight, 0.2)
hemisphereLightHelper.visible = false
scene.add(hemisphereLightHelper)
hemisphereLightFolder.add(hemisphereLightHelper, 'visible').name('Helper')


const directionalLightHelper = new THREE.DirectionalLightHelper(directionalLight, 0.2)
directionalLightHelper.visible = false
scene.add(directionalLightHelper)
directionalLightFolder.add(directionalLightHelper, 'visible').name('Helper')


const pointLightHelper = new THREE.PointLightHelper(pointLight, 0.2)
pointLightHelper.visible = false
scene.add(pointLightHelper)
pointLightFolder.add(pointLightHelper, 'visible').name('Helper')

const spotLightHelper = new THREE.SpotLightHelper(spotLight)
spotLightHelper.visible = false
scene.add(spotLightHelper)
spotLightFolder.add(spotLightHelper, 'visible').name('Helper')

const rectAreaLightHelper = new RectAreaLightHelper(rectAreaLight)
rectAreaLightHelper.visible = false
scene.add(rectAreaLightHelper)
rectAreaLightFolder.add(rectAreaLightHelper, 'visible').name('Helper')



/**
 * Objects
 */
// Material
const material = new THREE.MeshStandardMaterial()
material.roughness = 0.4

// Objects
const sphere = new THREE.Mesh(
    new THREE.SphereGeometry(0.5, 32, 32),
    material
)
sphere.position.x = - 1.5

const cube = new THREE.Mesh(
    new THREE.BoxGeometry(0.75, 0.75, 0.75),
    material
)

const torus = new THREE.Mesh(
    new THREE.TorusGeometry(0.3, 0.2, 32, 64),
    material
)
torus.position.x = 1.5

const plane = new THREE.Mesh(
    new THREE.PlaneGeometry(5, 5),
    material
)
plane.rotation.x = - Math.PI * 0.5
plane.position.y = - 0.65

scene.add(sphere, cube, torus, plane)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () => {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 1
camera.position.y = 1
camera.position.z = 2
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    // Update objects
    sphere.rotation.y = 0.1 * elapsedTime
    cube.rotation.y = 0.1 * elapsedTime
    torus.rotation.y = 0.1 * elapsedTime

    sphere.rotation.x = 0.15 * elapsedTime
    cube.rotation.x = 0.15 * elapsedTime
    torus.rotation.x = 0.15 * elapsedTime

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()